import React, { useState, useEffect, useMemo, useRef } from "react";
import useApi from "../../hooks/useApiGetPost";
import {
  Chip,
  Button,
  Input,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableFooter,
  Autocomplete,
  TextField,
} from "@mui/material";

// Placeholder imports (assuming these exist in the actual project structure)
import useApiAthul from "../../hooks/useApi";
import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";

// 🚨 NEW COMPONENT IMPORTS
import SalesTableGrouped from "./SalesTableGrouped";
import SalesTableFlatCASH from "./SalesTableFlatCASH";
import SalesTableMonthly from "./SalesTableMonthly";
import SalesTableAggregated from "./SalesTableAggregated";
import SalesTableDefault from "./SalesTableDefault";

import SalesTableGroupedPDF from "./SalesTableGroupedPDF";
import SalesTableAggregatedPDF from "./SalesTableAggregatedPDF";
import SalesTableMonthlyPDF from "./SalesTableMonthlyPDF";
import SalesTableFlatCASHPDF from "./SalesTableFlatCASHPDF";
import SalesTableDefaultPDF from "./SalesTableDefaultPDF";
import SalesTableCustomerGroupwiseAggregatedPDF from "./SalesTableCustomerGroupwiseAggregatedPDF";
import SalesTableAccountAggregatedPDF from "./SalesTableAccountAggregatedPDF";
import SalesTableSalespersonAggregatedPDF from "./SalesTableSalespersonAggregatedPDF";
import SalesTableGroupAggregatedPDF from "./SalesTableGroupAggregatedPDF";
import SalesTableBrandAggregatedPDF from "./SalesTableBrandAggregatedPDF";
import SalesTableDepartmentAggregatedPDF from "./SalesTableDepartmentAggregatedPDF";
import SalesTableDepartmentGroupAggregatedPDF from "./SalesTableDepartmentGroupAggregatedPDF";
import SalesTableGroupMonthMonthlyPDF from "./SalesTableGroupMonthMonthlyPDF";

import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import ReportDownloadButton from "./ReportDownloadButton";
import SalesTableAccountAggregated from "./SalesTableAccountAggregated";
import SalesTableSalespersonAggregated from "./SalesTableSalespersonAggregated";
import SalesTableGroupAggregated from "./SalesTableGroupAggregated";
import SalesTableBrandAggregated from "./SalesTableBrandAggregated";
import SalesTableDepartmentAggregated from "./SalesTableDepartmentAggregated";
import SalesTableFlatCASHReturn from "./SalesTableFlatCASHReturn";
import SalesTableDepartmentGroupAggregated from "./SalesTableDepartmentGroupAggregated";
import SalesTableSalespersonGroupAggregated from "./SalesTableSalespersonGroupAggregated";
import SalesTableGroupMonthMonthly from "./SalesTableGroupMonthMonthly";
import SalesTableCustomerGroupwiseAggregated from "./SalesTableCustomerGroupwiseAggregated";
import SalesTableFlatCASHReturnPDF from "./SalesTableFlatCASHReturnPDF";
import SalesTableSalespersonGroupAggregatedPDF from "./SalesTableSalespersonGroupAggregatedPDF";
import ReportExcelButton from "./ReportExcelButton";

// --- Utility Functions (Keep these in your main component or move to a shared file) ---

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const isNumericHeader = (header) =>
  ["AMT", "COMMISSION", "SALES", "RETURN", "VAT", "TOTAL", "%"].some((s) =>
    header.toUpperCase().includes(s)
  );

// --- Helper Functions (Stubs for the original dynamic logic) ---

// Placeholder for logic that determines the key for the first column in aggregated modes
const getDescriptiveColumnKey = (mode, data) => {
  if (!data || data.length === 0) return "CODE";

  if (mode === "SALES_CUSTOMER") return "ACCOUNT";
  if (mode === "SALES_SALESMAN") return "SALESPERSON";
  // Add logic for other aggregated modes here
  return (
    Object.keys(data[0]).find(
      (key) =>
        !isNumericHeader(key) &&
        !["isGroupHeader", "isSubHeader", "totalAmount"].includes(key)
    ) || "CODE"
  );
};

// Placeholder for grandColumnTotals calculation
const calculateGrandTotals = (data) => {
  if (!data || data.length === 0) return {};
  const totals = {};
  const firstRow = data[0];

  Object.keys(firstRow).forEach((key) => {
    // Simple check: if the key contains numbers in the first row, aggregate it
    if (typeof firstRow[key] === "number" || isNumericHeader(key)) {
      totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    }
  });
  return totals;
};

// Placeholder for page and rowsPerPage logic (if you decide to implement this in the parent)
const usePagination = (data) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Slice the data for the current page
  const paginatedRows = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return {
    paginatedRows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

const useScrollLoading = (data) => {
  // 25 is the default number of rows to load at once
  const defaultRowsPerPage = 1000;
  const [rowsToDisplay, setRowsToDisplay] = useState(defaultRowsPerPage);

  // Function to load the next set of data
  const loadNextPage = () => {
    setRowsToDisplay((prev) =>
      Math.min(prev + defaultRowsPerPage, data.length)
    );
  };

  // Reset the loaded count whenever the underlying filtered data array changes
  useEffect(() => {
    // Reset rows to display if the data is available, otherwise set to 0.
    setRowsToDisplay(data.length > 0 ? defaultRowsPerPage : 0);
  }, [data.length]);

  const paginatedRows = data.slice(0, rowsToDisplay);
  const hasMore = rowsToDisplay < data.length;

  return { paginatedRows, loadNextPage, hasMore, totalRows: data.length };
};

const SalesAnalysisReport = () => {
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [{ API_CONFIG: "https://cubixweberp.com:213" }];
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  let BASE_URL = "https://cubixweberp.com:213";

  console.log("API_PATHS -->", API_PATHS);
  if (API_PATHS) {
    if (API_PATHS?.length > 0) {
      BASE_URL = API_PATHS[0].API_CONFIG;
    }
  }

  const { request, loading } = useApiAthul();

  const [showItemList, setShowItemList] = useState(false);

  // --- State Variables ---
  const [searchTextTable, setSearchTextTable] = useState("");
  const [currentSelectedItem, setCurrentSelectedItem] = useState("-");
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [searchBy, setSearchBy] = useState("UNIVERSAL");

  const salesModes = [
    { label: "Sales Analysis", value: "SALESLIST" },
    { label: "Sales Analysis with Returns", value: "SALESLISTWITHRET" },
    { label: "Cash Sales Only", value: "SALESLIST_CASH" },
    { label: "Credit Sales Only", value: "SALESLIST_CREDIT" },
    { label: "Itemwise Sales", value: "SALES_ITEMWISE" },
    { label: "Itemwise Sales with Returns", value: "SALES_ITEMWISE_RET" },
    { label: "Customerwise Sales", value: "SALES_CUSTOMER" },
    { label: "Salesmanwise Sales", value: "SALES_SALESMAN" },
    { label: "Groupwise Sales", value: "SALES_GROUP" },
    { label: "Categorywise Sales", value: "SALES_CATEGORY" },
    { label: "Departmentwise Sales", value: "SALES_DEPT" },
    { label: "Departmentwise Sales (Transaction)", value: "SALES_DEPT_TRANS" },
    { label: "Customer Monthwise Sales", value: "SALES_MONTH" },
    { label: "Daily Movement Analysis", value: "DAILY_MOVEMENT" },
    { label: "Sales Return List", value: "RETURNLIST" },
    { label: "Cash Returns", value: "RETURNLIST_CASH" },
    { label: "Credit Returns", value: "RETURNLIST_CREDIT" },
    { label: "Itemwise Returns", value: "RETURN_ITEMWISE" },
    { label: "Minus Sales Invoice", value: "SALESLIST_MINUS" },
    { label: "Minus Sales Items", value: "SALES_ITEMWISE_MINUS" },
    { label: "Dept-Group wise", value: "SALES_DEPT_GROUP" },
    { label: "Dept-SubGroup wise", value: "SALES_DEPT_SGROUP" },
    { label: "Salesperson-Group wise", value: "SALES_SMAN_GROUP" },
    { label: "Salesperson-SubGroup wise", value: "SALES_SMAN_SGROUP" },
    { label: "Salesperson-Category wise", value: "SALES_SMAN_CATEG" },
    { label: "Group Monthwise Sales", value: "GROUP_MONTH" },
    { label: "SubGroup Monthwise Sales", value: "SGROUP_MONTH" },
    { label: "Category Monthwise Sales", value: "CATEG_MONTH" },
    { label: "Item Monthwise (Qty)", value: "ITEM_MONTH_QTY" },
    { label: "Item Monthwise (Value)", value: "ITEM_MONTH_VALUE" },
    { label: "Customer Groupwise Sales", value: "SALES_CUSTOMERGROUP" },
  ];

  const [currentSelectedModeObject, setCurrentSelectedModeObject] =
    useState(null);
  const [currentSelectedDepartment, setCurrentSelectedDepartment] =
    useState("-");
  const [userDepartmentList, setUserDepartment] = useState(null);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState("-");
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] =
    useState("-");
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState("-");
  const [currentSelectedSubGroup, setCurrentSelectedSubGroup] = useState("-");

  const {
    loading: loadinggetSubCategory,
    data: datagetSubCategory,
    get: getSubCategory,
  } = useApi();
  const {
    loading: loadinggetCategory,
    data: datagetCategory,
    get: getCategory,
  } = useApi();
  const {
    loading: loadinggetSubgroup,
    data: datagetSubgroup,
    get: getSubgroup,
  } = useApi();
  const {
    loading: loadinggetgroup,
    data: datagetgroup,
    get: getgroup,
  } = useApi();

  const {
    data: datagetSalesOrderReportAPI,
    loading: loadinggetSalesOrderReportAPI,
    get: getSalesOrderReportAPI,
  } = useApi();
  const { data: datagetgetFinancialDateApi, get: getFinancialDateApi } =
    useApi();
  const { data: datagetJobList, get: getJobList } = useApi();

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [jobCode, setJobCode] = useState(null);
  const [chipSearch, setChipSearch] = useState("");

  // --- Effects & Handlers ---

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    setUserDepartment(currentUser || null);

    setCurrentSelectedDepartment(
      currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-"
    );

    const today = new Date().toLocaleDateString("en-CA");
    setDateFrom(today);
    setDateTo(today);

    getCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/category/-/-`
    );
    getSubCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subcategory/-/-`
    );
    getgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/group/-/-`
    );
    getSubgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subgroup/-/-`
    );
    getJobList(
      `${API_PATHS[0].API_CONFIG}/api/cpayscount/${COMPANY_CODE}/joblist/-/-`
    );

    handleChipClick({ label: "Sales Analysis", value: "SALESLIST" });
  }, []);

  const handleChipClick = (chip) => {
    setCurrentSelectedModeObject(chip);
    setSelectedFilters((prev) => (prev.includes(chip) ? [] : [chip]));
  };

  const resetFilters = () => {
    // FINANCIAL YEAR START DATE LOGIC
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }

    const today = new Date().toLocaleDateString("en-CA");
    setDateTo(today);

    handleChipClick({ label: "Sales Analysis", value: "SALESLIST" });
    setItemSearch("");
    setCurrentSelectedGroup("-");
    setCurrentSelectedSubGroup("-");
    setCurrentSelectedCategory("-");
    setCurrentSelectedSubCategory("-");
    setJobCode(null);
    setChipSearch("");
  };

  const filteredChips = salesModes.filter((chip) =>
    chip.label.toLowerCase().includes(chipSearch.toLowerCase())
  );

  useEffect(() => {
    if (
      currentSelectedModeObject &&
      dateFrom &&
      dateTo &&
      currentSelectedDepartment
    ) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";
      let job_code_to_pass_to_api = jobCode ? jobCode.JobCode : "-";
      let itemSearchToAPI =
        currentSelectedItem != "-" ? currentSelectedItem?.Code?.trim() : "-";

      let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMSalesAnalysis/${COMPANY_CODE}/${mode_to_pass_to_api}/${job_code_to_pass_to_api}/-/-/${dateFrom}/${dateTo}/${currentSelectedCategory}/${currentSelectedGroup}/${currentSelectedDepartment}/-/${currentSelectedSubGroup}/-/-/${currentSelectedSubCategory}/-/-/${itemSearchToAPI}`;

      getSalesOrderReportAPI(apiUrl);
    }
  }, [
    currentSelectedModeObject,
    dateFrom,
    dateTo,
    currentSelectedDepartment,
    currentSelectedCategory,
    currentSelectedGroup,
    currentSelectedSubGroup,
    currentSelectedSubCategory,
    currentSelectedItem,
    jobCode,
  ]);

  useEffect(() => {
    getFinancialDateApi(
      `${API_PATHS[0].API_CONFIG}/api/Cpayscount/${COMPANY_CODE}/companyinfo/-/-`
    );
  }, []);

  // FINANCIAL YEAR START DATE LOGIC
  useEffect(() => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }
  }, [datagetgetFinancialDateApi]);

  const fetchProductData = async (
    query = "-",
    searchByValue = searchBy,
    location = "A"
  ) => {
    const formattedQuery = query.replace(/\//g, "%2F");
    try {
      const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
      const response = await request({ url, method: "GET", timeout: 5000 });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch product data", err);
      return [];
    }
  };

  const getInitialProductData = async () => {
    setModalSuggestions(await fetchProductData("-"));
  };
  useEffect(() => {
    getInitialProductData();
  }, []);

  const handleModalSearchQueryChange = async (query = "-") => {
    setItemSearch(query);
    setModalSuggestions(await fetchProductData(query));
  };

  useEffect(() => {
    if (itemSearch && itemSearch !== currentSelectedItem?.Code) {
      handleModalSearchQueryChange(itemSearch.trim());
    }
  }, [itemSearch]);

  const getTotalColumns = (data) => {
    if (!data || data.length === 0) return 1; // Default to 1 if no data

    // Dynamically determine all unique keys from the first row
    const allKeys = Object.keys(data[0]);

    // Exclude internal/helper keys used during grouping/aggregation
    const descriptiveColumnKey = getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      data
    );

    const excludedKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      descriptiveColumnKey,
    ];

    // Count the descriptive column (1) + all other data columns
    const displayKeys = allKeys.filter((key) => !excludedKeys.includes(key));

    // The total is the sticky column + the rest of the dynamic columns
    return 1 + displayKeys.length;
  };

  // --- Core Logic (Grouping, Filtering, Totals) ---

  const reportData = datagetSalesOrderReportAPI || [];

  // The data passed to the child component must be the final, filtered/grouped array.
  const filteredRows = useMemo(() => {
    console.log("reportData inside  filteredRows---->", reportData);
    if (!currentSelectedModeObject || reportData.length === 0) return [];
    const mode = currentSelectedModeObject.value;

    // 1. Grouped Modes (Hierarchical)
    if (["SALESLISTWITHRET", "SALESLIST_MINUS"].includes(mode)) {
      // Assumes your grouping utility handles the full hierarchy and returns a flat array for rendering
      return groupSalesThreeLevels(reportData);
    }

    // 2. Simple Filtering (for all other modes)
    // Apply your existing searchTextTable filter logic here if needed, or pass the full data.
    let finalData = reportData;

    if (searchTextTable) {
      const search = searchTextTable.toLowerCase();
      finalData = reportData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search)
        )
      );
    }

    return finalData;
  }, [reportData, currentSelectedModeObject, searchTextTable]);

  // Calculate totals only on the raw report data or final filtered data
  const grandColumnTotals = useMemo(() => {
    return calculateGrandTotals(reportData);
  }, [reportData]);

  const descriptiveColumnKey = useMemo(() => {
    return getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      reportData
    );
  }, [currentSelectedModeObject, reportData]);

  // --- Component Mapping ---

  const TableToPDFMap = new Map([
    [SalesTableGrouped, SalesTableGroupedPDF],
    [SalesTableFlatCASH, SalesTableFlatCASHPDF],
    [SalesTableMonthly, SalesTableMonthlyPDF],
    [SalesTableAggregated, SalesTableAggregatedPDF],
    [SalesTableDefault, SalesTableDefaultPDF],
    [
      SalesTableCustomerGroupwiseAggregated,
      SalesTableCustomerGroupwiseAggregatedPDF,
    ],
    [SalesTableAccountAggregated, SalesTableAccountAggregatedPDF],
    [SalesTableSalespersonAggregated, SalesTableSalespersonAggregatedPDF],
    [SalesTableGroupAggregated, SalesTableGroupAggregatedPDF],
    [SalesTableBrandAggregated, SalesTableBrandAggregatedPDF],
    [SalesTableDepartmentAggregated, SalesTableDepartmentAggregatedPDF],
    [SalesTableFlatCASHReturn, SalesTableFlatCASHReturnPDF],
    [
      SalesTableDepartmentGroupAggregated,
      SalesTableDepartmentGroupAggregatedPDF,
    ],
    [
      SalesTableSalespersonGroupAggregated,
      SalesTableSalespersonGroupAggregatedPDF,
    ],
    [SalesTableGroupMonthMonthly, SalesTableGroupMonthMonthlyPDF],
  ]);

  const ReportTableMap = useMemo(
    () => ({
      // 1. GROUPED MODES (Hierarchical 3-level)

      SALESLISTWITHRET: SalesTableGrouped,
      SALESLIST_MINUS: SalesTableGrouped,

      // 2. SPECIFIC FLAT MODES (14-column fixed)
      SALESLIST_CASH: SalesTableFlatCASH,
      SALESLIST_CREDIT: SalesTableFlatCASH,
      RETURNLIST_CASH: SalesTableFlatCASHReturn,
      RETURNLIST_CREDIT: SalesTableFlatCASHReturn,
      RETURNLIST: SalesTableFlatCASHReturn,

      // 3. MONTHLY MODES (18-column fixed structure)
      SALES_MONTH: SalesTableMonthly,
      GROUP_MONTH: SalesTableGroupMonthMonthly,
      SGROUP_MONTH: SalesTableGroupMonthMonthly,
      CATEG_MONTH: SalesTableGroupMonthMonthly,
      ITEM_MONTH_QTY: SalesTableGroupMonthMonthly,
      ITEM_MONTH_VALUE: SalesTableGroupMonthMonthly,

      // 4. AGGREGATED MODES (Dynamic Columns, 1 sticky column)
      SALES_ITEMWISE: SalesTableAggregated,
      SALES_ITEMWISE_RET: SalesTableAggregated,
      SALES_CUSTOMER: SalesTableAccountAggregated,
      SALES_SALESMAN: SalesTableSalespersonAggregated,
      SALES_GROUP: SalesTableGroupAggregated,
      SALES_CATEGORY: SalesTableBrandAggregated,
      SALES_DEPT: SalesTableDepartmentAggregated,
      RETURN_ITEMWISE: SalesTableAggregated,
      SALES_ITEMWISE_MINUS: SalesTableAggregated,
      SALES_DEPT_GROUP: SalesTableDepartmentGroupAggregated,
      SALES_DEPT_SGROUP: SalesTableDepartmentGroupAggregated,
      SALES_SMAN_GROUP: SalesTableSalespersonGroupAggregated,
      SALES_SMAN_SGROUP: SalesTableSalespersonGroupAggregated,
      SALES_SMAN_CATEG: SalesTableSalespersonGroupAggregated,
      SALES_CUSTOMERGROUP: SalesTableCustomerGroupwiseAggregated,

      // 5. SIMPLE FLAT/DEFAULT MODES
      SALESLIST: SalesTableDefault,
      SALES_DEPT_TRANS: SalesTableDefault, // Placeholder - use this if data structure is not grouped/aggregated
      DAILY_MOVEMENT: SalesTableDefault, // Placeholder
    }),
    []
  );

  const clickedOnPrint = async () => {
    const currentSelectedMode = currentSelectedModeObject.value;

    // A. Get the original Table Component using the report mode string
    const ReportComponent = ReportTableMap[currentSelectedMode];

    // B. Look up the corresponding PDF Component using the TableToPDFMap
    const PDFComponent = ReportComponent
      ? TableToPDFMap.get(ReportComponent)
      : null;

    if (!filteredRows || filteredRows?.length === 0) return;
    if (!PDFComponent) return;

    console.log("filteredRows clickedOnPrint!!", filteredRows, PDFComponent);

    // Open a blank window first to avoid popup block
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup blocked! Please allow popups for this site.");
      return;
    }

    // Render your PDF component
    const doc = (
      <PDFComponent
        rows={filteredRows}
        descriptiveColumnKey={descriptiveColumnKey}
        clientDetails={{
          name: "test",
          address: "test address",
          phone: "1234567890",
          email: "test@email",
          footerText: "test footer",
        }}
      />
    );

    // Generate PDF as blob
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Create blob URL
    const blobUrl = URL.createObjectURL(blob);

    // Load the PDF in the blank window
    printWindow.location.href = blobUrl;

    // Optional: wait until the PDF loads, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const CurrentTableComponent = currentSelectedModeObject
    ? ReportTableMap[currentSelectedModeObject.value]
    : null;

  // --- Pagination (Remains in Parent) ---
  // const { paginatedRows, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(filteredRows);

  const { paginatedRows, loadNextPage, hasMore, totalRows } =
    useScrollLoading(filteredRows);

  // const scrollContainerRef = useRef(null);

  // // --- Scroll Handler ---
  // const handleScroll = () => {

  //     console.log("handleScroll outside  --->")

  //     const currentTarget = scrollContainerRef.current;

  //     // Only trigger if we aren't already loading and there is more data to load
  //     if (!currentTarget || loadinggetSalesOrderReportAPI || !hasMore) return;

  //     const { scrollTop, scrollHeight, clientHeight } = currentTarget;

  //     console.log("handleScroll inside --->")

  //     // Check if user scrolled within 1px of the bottom
  //     if (scrollHeight - scrollTop <= clientHeight + 10) {
  //         loadNextPage();
  //     }
  // };

  const totalColumnsCount = getTotalColumns(paginatedRows); // Calculate the number

  const clickedOnExport = async () => {
    // Check if running on the client (browser) before attempting DOM operations
    if (typeof document === "undefined") {
      console.warn(
        "PDF Export called in a non-browser environment (SSR). Skipping download."
      );
      return;
    }

    const currentSelectedMode = currentSelectedModeObject.value;

    // A. Get the original Table Component
    const ReportComponent = ReportTableMap[currentSelectedMode];

    // B. Look up the corresponding PDF Component
    const PDFComponent = ReportComponent
      ? TableToPDFMap.get(ReportComponent)
      : null;

    if (!PDFComponent) {
      console.error(`No PDF component found for mode: ${currentSelectedMode}`);
      return;
    }

    // 1. Render the PDF Document instance using the imported component
    const documentInstance = // Renamed to avoid confusion with browser's 'document'
      (
        <PDFComponent
          rows={filteredRows}
          descriptiveColumnKey={descriptiveColumnKey}
          clientDetails={{
            name: "test",
            address: "test address",
            phone: "1234567890",
            email: "test@email",
            footerText: "test footer",
          }}
        />
      );

    try {
      // 2. Use the pdf() utility to generate the blob
      const blob = await pdf(documentInstance).toBlob();

      // 3. Create a temporary URL and link element to trigger the download
      const url = URL.createObjectURL(blob);

      // Use a single, correct declaration of 'link' (DOM operation is safe here)
      const link = document.createElement("a");

      // Define the file name
      link.download = `SalesReport_${currentSelectedMode}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      link.href = url;

      // 4. Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error("Error during PDF generation or download:", error);
    }
  };

  return (
    <div style={{ height: "calc(100vh - 200px)" }}>
      {/* Header panel Omitted for Brevity */}
      <div className="relative overflow-hidden rounded-xl shadow-md mx-4 my-1">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 opacity-90" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
          }}
        />
        <div className="relative px-6 py-6 sm:px-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md">
              Sales Analysis Report
            </h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={clickedOnPrint}
              className="flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md text-sm font-medium transition duration-200 backdrop-blur-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.8"
                stroke="currentColor"
                className="w-5 h-5 mr-1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 9V2h12v7m-9 8h6m-9 4h12v-4H6v4zm-1 0a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5z"
                />
              </svg>
              Print
            </button>
            {/* <button
                            onClick={clickedOnExport}
                            className="flex items-center px-3 py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-md text-sm font-medium transition duration-200"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor" className="w-5 h-5 mr-1 text-indigo-600"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-4-4-4 4m0 0-4-4m4 4V4" /></svg>
                            Export
                        </button> */}
            {currentSelectedModeObject && filteredRows && (
              <>
                <ReportDownloadButton
                  ReportPDF={SalesTableGroupedPDF}
                  currentSelectedModeObject={currentSelectedModeObject}
                  filteredRows={filteredRows}
                  descriptiveColumnKey={descriptiveColumnKey}
                  grandColumnTotals={grandColumnTotals}
                  clientDetails={{
                    name: "test",
                    address: "test address",
                    phone: "1234567890",
                    email: "test@email",
                    footerText: "test footer",
                  }}
                >
                  Download
                </ReportDownloadButton>

                <ReportExcelButton
                  filteredRows={filteredRows}
                  reportTitle={`SalesReport_${
                    currentSelectedModeObject.value
                  }_${new Date().toISOString().split("T")[0]}`}
                  grandColumnTotals={grandColumnTotals}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: "18%",
            maxWidth: "260px",
            padding: 2,
            borderRight: "1px solid #ccc",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <div className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-1.5 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 mb-3">
            <svg
              className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search modes"
              value={chipSearch}
              onChange={(e) => setChipSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 focus:ring-0"
            />
          </div>
          <div
            style={{
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
              paddingRight: "4px",
            }}
            className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500 transition-all duration-300"
          >
            {filteredChips.map((chip, index) => (
              <div key={index}>
                <Chip
                  title={chip.label}
                  label={chip.label}
                  clickable
                  color={
                    selectedFilters.filter(
                      (item) => item.value === chip.value
                    )[0]?.value
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleChipClick(chip)}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      textAlign: "left",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      width: "100%",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    },
                  }}
                />
              </div>
            ))}
          </div>
        </Box>

        {/* Right Panel (Main Content & Table) */}
        <Box
          sx={{
            width: "82%",
            paddingLeft: 3,
            paddingRight: 3,
            paddingTop: 2,
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Paper
            sx={{ padding: 1, borderRadius: "8px", backgroundColor: "#fff" }}
          >
            {/* Top Filters Omitted for Brevity */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                style={{
                  display: "flex",
                  marginLeft: "10px",
                  marginTop: "5px",
                }}
              >
                <Grid item>
                  <Input
                    fullWidth
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid item style={{ marginLeft: "10px" }}>
                  <Input
                    fullWidth
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Grid>
              </div>

              <div style={{ display: "flex", marginTop: "5px" }}>
                <Grid item>
                  <Select
                    fullWidth
                    value={currentSelectedDepartment}
                    onChange={(e) =>
                      setCurrentSelectedDepartment(e.target.value)
                    }
                    displayEmpty
                    sx={{
                      height: 36,
                      fontSize: 14,
                      "& .MuiSelect-select": { paddingY: 0.5 },
                    }}
                  >
                    <MenuItem value="-">Select Department</MenuItem>
                    {userDepartmentList?.map((group, index) => (
                      <MenuItem key={index} value={group.DEPTNO?.trim()}>
                        {group.DEPTNO?.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item style={{ marginLeft: "10px" }}>
                  <Input
                    fullWidth
                    placeholder="Item"
                    onKeyDown={(e) => {
                      if (e.key === "F2") {
                        e.preventDefault();
                        setShowItemList(true);
                      }
                    }}
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                  />
                  {itemSearch?.length > 0 && modalSuggestions?.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 10,
                        height: "200px",
                        width: "200px",
                        overflowY: "scroll",
                        backgroundColor: "#f7f7f7",
                      }}
                    >
                      {modalSuggestions?.map((item, i) => {
                        return (
                          <p
                            key={i}
                            onClick={() => {
                              setCurrentSelectedItem(item);
                              setItemSearch(item.Code);
                              setModalSuggestions(null);
                            }}
                            style={{
                              padding: "10px",
                              border: "1px solid #dedede",
                            }}
                          >
                            {item.Code}
                          </p>
                        );
                      })}
                    </div>
                  )}
                </Grid>
              </div>

              <div style={{ display: "flex", justifyContent: "end" }}>
                <Box sx={{ marginRight: 3, display: "flex" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={resetFilters}
                    sx={{
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsFilterPanelVisible((prev) => !prev)}
                    sx={{
                      marginLeft: "10px",
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Filters
                  </Button>
                </Box>
              </div>
            </div>

            {/* Expanded Filters */}
            {isFilterPanelVisible && (
              <div>
                <Grid container spacing={2} sx={{ marginTop: 1 }}>
                  {/* Group Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetgroup || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetgroup}
                        value={
                          datagetgroup?.find(
                            (g) => g["GRP CODE"] === currentSelectedGroup
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedGroup(
                            newValue ? newValue["GRP CODE"] : "-"
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Group"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetgroup ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* SubGroup Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetSubgroup || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetSubgroup}
                        value={
                          datagetSubgroup?.find(
                            (g) => g["GRP CODE"] === currentSelectedSubGroup
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedSubGroup(
                            newValue ? newValue["GRP CODE"] : "-"
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select SubGroup"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetSubgroup ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* Category Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetCategory || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetCategory}
                        value={
                          datagetCategory?.find(
                            (g) => g["GRP CODE"] === currentSelectedCategory
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedCategory(
                            newValue ? newValue["GRP CODE"] : "-"
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Category"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetCategory ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* SubCategory Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetSubCategory || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetSubCategory}
                        value={
                          datagetSubCategory?.find(
                            (g) => g["GRP CODE"] === currentSelectedSubCategory
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedSubCategory(
                            newValue ? newValue["GRP CODE"] : "-"
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select SubCategory"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetSubCategory ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* JobCode (already Autocomplete) */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetJobList || []}
                        getOptionLabel={(option) =>
                          option?.JobCode ? `${option.Description || ""}` : ""
                        }
                        value={jobCode}
                        onChange={(event, newValue) => setJobCode(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Job"
                            placeholder="Type to search..."
                            size="small"
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#000000" }}
                            >
                              {option.Description}
                            </Typography>
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.JobCode === value.JobCode
                        }
                      />
                    </Box>
                  </Grid>

                  {/* Group View Checkbox */}
                  {/* <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={groupViewCheckbox}
                                                    onChange={(e) => setGroupViewCheckbox(e.target.checked)}
                                                />
                                            }
                                            label="Group View"
                                        />
                                    </Grid> */}
                </Grid>
              </div>
            )}
          </Paper>

          {/* Table Section */}
          <Paper
            sx={{
              width: "100%",
              marginTop: 2,
              // 4. 🚨 CRITICAL FIX: This container must take the remaining vertical space
              //    and control the flow of its children.
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* 🚨 Dynamic Table Rendering Block */}
            <Box
              // ref={scrollContainerRef}
              sx={{
                p: 1,
                flexGrow: 1, // Take all available height
                overflowY: "auto", // Enable vertical scrolling here
                minHeight: 0, // Crucial for flex scrolling
              }}
              // onScroll={handleScroll} // 🚨 ADD SCROLL HANDLER
            >
              {CurrentTableComponent ? (
                <CurrentTableComponent
                  // Pass all necessary data and helpers to the selected component
                  rows={paginatedRows} // Use paginated rows for the component
                  grandColumnTotals={grandColumnTotals}
                  loading={loadinggetSalesOrderReportAPI}
                  formatNumber={formatNumber}
                  mode={currentSelectedModeObject.value} // Required by some children
                  // Props specific to certain types
                  groupSalesThreeLevels={groupSalesThreeLevels} // For Grouped
                  descriptiveColumnKey={descriptiveColumnKey} // For Aggregated
                  totalColumnsCount={totalColumnsCount}
                  hasMore={hasMore}
                  loadNextPage={loadNextPage}
                />
              ) : (
                // Render a default state if no mode is selected
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="h6" color="textSecondary">
                    Please select a report mode from the left panel.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ paddingX: 2, paddingTop: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                Total Rows Found: {filteredRows.length}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default SalesAnalysisReport;
